/**
 * Portada del portal: qué le espera al paciente, sin tener que buscarlo.
 *
 * Abre por lo accionable —"tienes un resultado listo", "tu próxima cita es el
 * martes"— y no por un menú. Quien entra al portal viene con una pregunta
 * concreta en la cabeza, y casi siempre es una de esas dos.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CalendarDays, FlaskConical, Pill, Users } from "lucide-react";
import { misCitas } from "../../api/portal";
import { usePortalAuth } from "../../auth/PortalAuthProvider";
import { useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { fechaHora } from "../../lib/fecha";
import { Alert, Badge, Card, CardBody } from "../../components/ui";
import { CambiarPasswordCard } from "./CambiarPasswordCard";

export function PortalInicioPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { me } = usePortalAuth();
  const tz = useZonaHoraria();

  const { data: citas = [] } = useQuery({
    queryKey: ["portal", "citas", true],
    queryFn: () => misCitas(true),
  });

  const proxima = citas[0];
  const n = me?.novedades ?? {};

  return (
    <div className="space-y-5">
      {/* Lo primero, si entró con la clave del mostrador: cambiarla. */}
      {me?.debe_cambiar_password && <CambiarPasswordCard />}

      <div>
        <h1 className="text-xl font-bold text-slate-900">
          Hola, {me?.yo.nombre_completo.split(" ")[0]}
        </h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Esto es lo que tienes en {me?.policlinico}.
        </p>
      </div>

      {/* La próxima cita es el dato que más se viene a buscar. */}
      <Card>
        <CardBody>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            <CalendarDays className="h-4 w-4" />
            Tu próxima cita
          </p>
          {proxima ? (
            <div className="mt-2">
              <p className="text-lg font-semibold text-slate-900">
                {fechaHora(proxima.inicio, tz)}
              </p>
              <p className="text-sm text-slate-600">
                {proxima.especialidad}
                {proxima.profesional ? ` · ${proxima.profesional}` : ""}
              </p>
              {proxima.sede && (
                <p className="text-sm text-slate-500">{proxima.sede}</p>
              )}
              {!proxima.pagada && (
                <p className="mt-2 text-sm text-amber-700">
                  Pasa por caja antes de tu consulta.
                </p>
              )}
              {proxima.paciente_id !== me?.yo.paciente_id && (
                <Badge variant="neutral">{proxima.paciente_nombre}</Badge>
              )}
            </div>
          ) : (
            <p className="mt-2 text-sm text-slate-500">
              No tienes citas programadas. Para pedir una, llama o acércate a
              admisión.
            </p>
          )}
        </CardBody>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2">
        <Atajo
          to={`/${slug}/portal/resultados`}
          icon={FlaskConical}
          titulo="Resultados de laboratorio"
          detalle={
            n.resultados_listos
              ? `${n.resultados_listos} ${n.resultados_listos === 1 ? "listo" : "listos"} para ver`
              : "Nada nuevo por ahora"
          }
          destacado={!!n.resultados_listos}
        />
        <Atajo
          to={`/${slug}/portal/recetas`}
          icon={Pill}
          titulo="Mis recetas"
          detalle={
            n.recetas_pendientes
              ? `${n.recetas_pendientes} sin retirar`
              : "Ninguna pendiente"
          }
          destacado={!!n.recetas_pendientes}
        />
      </div>

      {/* Solo aparece si de verdad responde por alguien. */}
      {me && me.a_mi_cargo.length > 0 && (
        <Alert variant="info">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <Users className="h-4 w-4" />
            También ves lo de{" "}
            {me.a_mi_cargo.map((m) => m.nombre_completo).join(", ")}
          </span>
        </Alert>
      )}
    </div>
  );
}

function Atajo({
  to,
  icon: Icon,
  titulo,
  detalle,
  destacado,
}: {
  to: string;
  icon: typeof Pill;
  titulo: string;
  detalle: string;
  destacado: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 rounded-2xl border p-4 transition hover:shadow-sm ${
        destacado
          ? "border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_6%,white)]"
          : "border-slate-200 bg-white"
      }`}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          destacado
            ? "bg-[color:var(--color-link)] text-white"
            : "bg-slate-100 text-slate-500"
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="block font-medium text-slate-900">{titulo}</span>
        <span className="block text-sm text-slate-500">{detalle}</span>
      </span>
      <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-slate-400" />
    </Link>
  );
}
